/**
 * A finished Review run: summary counts, then every claim — contradictions
 * first — filterable by verdict. Each claim shows where it sits in the draft,
 * the draft vs source values when they differ, the verified evidence quotes
 * with their citations, and whether the verdict was downgraded because its
 * evidence could not be verified.
 */

import { useState } from "react";
import {
  Badge,
  Blockquote,
  Card,
  Group,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { CitationChip } from "@/features/solutions/shared";
import {
  VERDICT_COLOR,
  VERDICT_LABEL,
  sortClaims,
  type ReviewClaim,
  type ReviewResult,
  type Verdict,
} from "./types";

type Filter = "all" | Verdict;

function SummaryTile({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Paper withBorder radius="md" p="sm">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text size="xl" fw={700} c={color} aria-label={`${label} count`}>
        {value}
      </Text>
    </Paper>
  );
}

function Values({ claim }: { claim: ReviewClaim }) {
  if (!claim.draft_value && !claim.source_value) return null;
  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
      <Paper radius="sm" p="xs" bg="var(--mantine-color-red-light)">
        <Text size="xs" c="dimmed">
          Draft says
        </Text>
        <Text size="sm" fw={600} data-testid="draft-value">
          {claim.draft_value ?? "—"}
        </Text>
      </Paper>
      <Paper radius="sm" p="xs" bg="var(--mantine-color-green-light)">
        <Text size="xs" c="dimmed">
          Source says
        </Text>
        <Text size="sm" fw={600} data-testid="source-value">
          {claim.source_value ?? "—"}
        </Text>
      </Paper>
    </SimpleGrid>
  );
}

function ClaimCard({ claim }: { claim: ReviewClaim }) {
  return (
    <Card withBorder radius="md" p="sm" data-testid="review-claim">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <Badge variant="outline" color="gray" size="sm">
              {claim.id}
            </Badge>
            <Text size="sm">{claim.text}</Text>
          </Group>
          <Badge color={VERDICT_COLOR[claim.verdict]} variant="light">
            {VERDICT_LABEL[claim.verdict]}
          </Badge>
        </Group>
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            Draft {claim.draft_location.label}
          </Text>
          <Text size="xs" c="dimmed">
            · {claim.kind}
          </Text>
        </Group>
        <Values claim={claim} />
        <Text size="sm" c="dimmed">
          {claim.explanation}
        </Text>
        {claim.downgraded && (
          <Text size="xs" c="orange" data-testid="downgraded">
            Downgraded from{" "}
            {claim.model_verdict ? VERDICT_LABEL[claim.model_verdict] : "the model's verdict"}:{" "}
            {claim.downgrade_reason}
          </Text>
        )}
        {claim.error && (
          <Text size="xs" c="orange">
            Not checked — the search or the model failed for this claim.
          </Text>
        )}
        {claim.evidence.map((ev) => (
          <Stack key={`${ev.citation.chunk_id}-${ev.quote}`} gap={4}>
            <Blockquote p="xs" radius="sm" fz="sm">
              “{ev.quote}”
            </Blockquote>
            <Group gap="xs">
              <CitationChip citation={ev.citation} />
            </Group>
          </Stack>
        ))}
      </Stack>
    </Card>
  );
}

export function ReviewResultView({ result }: { result: ReviewResult }) {
  const [filter, setFilter] = useState<Filter>("all");
  const s = result.summary;
  const claims = sortClaims(result.claims).filter((c) => filter === "all" || c.verdict === filter);
  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
        <SummaryTile label="Contradicted" value={s.contradicted} color="red" />
        <SummaryTile label="Not found" value={s.not_found} color="gray" />
        <SummaryTile label="Supported" value={s.supported} color="green" />
        <SummaryTile label="Claims" value={s.total} color="inherit" />
      </SimpleGrid>
      {result.claims_truncated && (
        <Text size="sm" c="orange">
          The draft contained {result.claims_extracted} claims; only the first{" "}
          {result.claims.length} were checked.
        </Text>
      )}
      <SegmentedControl
        value={filter}
        onChange={(v) => setFilter(v as Filter)}
        aria-label="Filter claims by verdict"
        data={[
          { value: "all", label: `All (${s.total})` },
          { value: "contradicted", label: `Contradicted (${s.contradicted})` },
          { value: "not_found", label: `Not found (${s.not_found})` },
          { value: "supported", label: `Supported (${s.supported})` },
        ]}
      />
      <Stack gap="sm">
        {claims.map((c) => (
          <ClaimCard key={c.id} claim={c} />
        ))}
        {claims.length === 0 && (
          <Text size="sm" c="dimmed">
            No claims with this verdict.
          </Text>
        )}
      </Stack>
    </Stack>
  );
}
